import { zipSync } from 'fflate';
import { formatBytes, saveBlob } from './extra-common.js';
import { enhanceSelect } from '../core/custom-select.js';
import { trackEvent } from '../core/analytics.js';

const isAr = document.documentElement.lang === 'ar';
const common = {
  choose: isAr ? 'إضافة صور' : 'Add images',
  drop: isAr ? 'اسحب الصور هنا أو اخترها من جهازك' : 'Drop images here or choose them from your device',
  dropTitle: isAr ? 'اسحب الصور أو انقر لاختيارها من جهازك' : 'Drop images or click to choose from your device',
  dropSubtitle: isAr ? 'بلا حدود لعدد الصور · معالجة محلية آمنة 100% في متصفحك' : 'Unlimited images · 100% private local browser processing',
  defaults: isAr ? 'الإعدادات الافتراضية' : 'Default settings',
  apply: isAr ? 'تطبيق الإعدادات على الكل' : 'Apply settings to all',
  processAll: isAr ? 'معالجة الكل' : 'Process all',
  reprocessAll: isAr ? 'إعادة معالجة الكل' : 'Re-process all',
  retryFailed: isAr ? 'إعادة محاولة الفاشلة' : 'Retry failed',
  zip: isAr ? 'تنزيل النتائج ZIP' : 'Download results ZIP',
  clear: isAr ? 'مسح القائمة' : 'Clear list',
  customize: isAr ? 'تخصيص الإعدادات' : 'Customize settings',
  process: isAr ? 'معالجة' : 'Process',
  download: isAr ? 'تنزيل' : 'Download',
  remove: isAr ? 'إزالة' : 'Remove',
  queued: isAr ? 'في قائمة الانتظار' : 'Queued',
  working: isAr ? 'جارٍ المعالجة…' : 'Processing…',
  done: isAr ? 'اكتملت المعالجة' : 'Processing complete',
  failed: isAr ? 'تعذرت المعالجة' : 'Failed',
  invalid: isAr ? 'الملف غير مدعوم.' : 'Unsupported file type.',
  nothing: isAr ? 'أضف صورًا للبدء.' : 'Add images to get started.',
  zipReady: isAr ? 'تم تنزيل النتائج في ملف ZIP واحد.' : 'All results were downloaded in one ZIP file.',
  compare: isAr ? 'مقارنة' : 'Compare',
  before: isAr ? 'قبل' : 'Before',
  after: isAr ? 'بعد' : 'After',
  smaller: isAr ? 'أقل' : 'smaller',
  larger: isAr ? 'أكبر' : 'larger',
  saved: isAr ? 'توفير' : 'Saved',
  close: isAr ? 'إغلاق' : 'Close',
  compareTitle: isAr ? 'مقارنة الصورة قبل وبعد الضغط' : 'Compare Before & After Compression',
  dragToCompare: isAr ? 'اسحب المقبض يمينًا ويسارًا للمقارنة المباشرة بدقة 100%' : 'Drag the slider left and right to compare at 100% precision'
};

function getDeviceProcessingCapacity() {
  const cores = typeof navigator !== 'undefined' && navigator.hardwareConcurrency ? navigator.hardwareConcurrency : 2;
  const memory = typeof navigator !== 'undefined' && navigator.deviceMemory ? navigator.deviceMemory : 4;
  const isMobile = typeof navigator !== 'undefined' && (
    /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '') ||
    (typeof screen !== 'undefined' && screen.width < 768)
  );

  // Safe sequential execution (1 at a time) for weak devices or mobile to protect memory and avoid crashes
  if (memory <= 2 || cores <= 2 || (isMobile && cores <= 4)) {
    return { concurrency: 1, isLowEnd: true };
  }

  // Mid-tier devices: 2 concurrent tasks
  if (cores <= 4 || memory <= 4) {
    return { concurrency: 2, isLowEnd: false };
  }

  // High-performance multi-core devices: up to 3 concurrent tasks
  return { concurrency: Math.min(3, Math.floor(cores / 2)), isLowEnd: false };
}


function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function safeName(name) {
  return name.replace(/\.[^.]+$/, '').replace(/[^\p{L}\p{N}._-]+/gu, '_').slice(0, 100) || 'image';
}

function readField(field, value) {
  const isSegmented = field.type === 'segmented';
  const isCards = field.type === 'cards';
  const isCheckbox = field.type === 'checkbox';
  const isRange = field.type === 'range';
  const isSelect = field.type === 'select';

  let wrapper;
  let input;

  if (isCards) {
    wrapper = element('div', 'batch-setting batch-setting-cards');
    if (field.span === 'full') wrapper.classList.add('batch-setting-full');
    else if (field.span) wrapper.style.gridColumn = `span ${field.span}`;

    if (field.label) {
      wrapper.append(element('span', 'batch-setting-label', field.label));
    }

    input = element('input');
    input.type = 'hidden';
    input.value = value ?? field.default ?? (field.options?.[0]?.value || '');

    const grid = element('div', 'batch-cards-grid');
    grid.setAttribute('role', 'radiogroup');
    if (field.label) grid.setAttribute('aria-label', field.label);

    const cards = [];
    for (const option of field.options || []) {
      const card = element('button', 'batch-choice-card');
      card.type = 'button';
      card.setAttribute('role', 'radio');
      card.setAttribute('data-value', option.value);

      const isSelected = String(input.value) === String(option.value);
      card.classList.toggle('is-active', isSelected);
      card.setAttribute('aria-checked', isSelected ? 'true' : 'false');

      if (option.badge) {
        card.append(element('span', 'batch-choice-badge', option.badge));
      }

      if (option.iconSvg) {
        const iconWrap = element('div', 'batch-choice-icon');
        iconWrap.innerHTML = option.iconSvg;
        card.append(iconWrap);
      }

      const body = element('div', 'batch-choice-body');
      const title = element('strong', 'batch-choice-title', option.title || option.label || '');
      body.append(title);
      if (option.desc) {
        body.append(element('span', 'batch-choice-desc', option.desc));
      }
      card.append(body);

      const footer = element('div', 'batch-choice-footer');
      const statusSpan = element('span', 'batch-choice-status', option.statusText || '');
      if (option.statusId) statusSpan.id = option.statusId;
      if (option.initialState) statusSpan.setAttribute('data-state', option.initialState);

      const progressWrap = element('div', 'batch-choice-progress');
      if (option.progressId) progressWrap.id = option.progressId;
      progressWrap.style.display = 'none';
      const progressBar = element('div', 'batch-choice-progress-bar');
      progressWrap.append(progressBar);

      footer.append(statusSpan, progressWrap);
      card.append(footer);

      card.addEventListener('click', () => {
        if (input.disabled) return;
        input.value = option.value;
        for (const c of cards) {
          const active = c === card;
          c.classList.toggle('is-active', active);
          c.setAttribute('aria-checked', active ? 'true' : 'false');
        }
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      cards.push(card);
      grid.append(card);
    }

    input._updateCards = (val) => {
      input.value = val;
      for (let i = 0; i < field.options.length; i++) {
        const active = String(field.options[i].value) === String(val);
        cards[i]?.classList.toggle('is-active', active);
        cards[i]?.setAttribute('aria-checked', active ? 'true' : 'false');
      }
    };
    input._updatePills = input._updateCards;

    wrapper.append(grid, input);
    if (field.disclaimer) {
      const disclaimer = element('div', 'batch-setting-disclaimer');
      disclaimer.innerHTML = `
        <svg class="batch-disclaimer-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <div class="batch-disclaimer-content">${field.disclaimer}</div>
      `;
      wrapper.append(disclaimer);
    }
  } else if (isSegmented) {
    wrapper = element('div', 'batch-setting batch-setting-segmented');
    if (field.span === 'full') wrapper.classList.add('batch-setting-full');
    else if (field.span) wrapper.style.gridColumn = `span ${field.span}`;

    if (field.label) {
      wrapper.append(element('span', 'batch-setting-label', field.label));
    }

    input = element('input');
    input.type = 'hidden';
    input.value = value ?? field.default ?? (field.options?.[0]?.value || '');

    const track = element('div', 'batch-segmented-track');
    track.setAttribute('role', 'radiogroup');

    const pills = [];
    for (const option of field.options || []) {
      const pill = element('button', 'batch-segment-pill', option.label);
      pill.type = 'button';
      pill.setAttribute('role', 'radio');
      const isSelected = String(input.value) === String(option.value);
      if (isSelected) {
        pill.classList.add('is-active');
        pill.setAttribute('aria-checked', 'true');
      } else {
        pill.setAttribute('aria-checked', 'false');
      }

      pill.addEventListener('click', () => {
        if (input.disabled) return;
        input.value = option.value;
        for (const p of pills) {
          const active = p === pill;
          p.classList.toggle('is-active', active);
          p.setAttribute('aria-checked', active ? 'true' : 'false');
        }
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      pills.push(pill);
      track.append(pill);
    }

    input._updatePills = (val) => {
      input.value = val;
      for (let i = 0; i < field.options.length; i++) {
        const active = String(field.options[i].value) === String(val);
        pills[i]?.classList.toggle('is-active', active);
        pills[i]?.setAttribute('aria-checked', active ? 'true' : 'false');
      }
    };

    wrapper.append(track, input);
  } else if (isCheckbox) {
    wrapper = element('label', 'batch-setting batch-setting-switch');
    if (field.span === 'full') wrapper.classList.add('batch-setting-full');
    else if (field.span) wrapper.style.gridColumn = `span ${field.span}`;

    const infoWrap = element('div', 'batch-switch-info');
    const title = element('strong', 'batch-switch-title');
    title.innerHTML = `
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
      <span>${field.label}</span>
    `;
    infoWrap.append(title);
    if (field.hint) {
      infoWrap.append(element('small', 'batch-switch-hint', field.hint));
    }

    const toggleWrap = element('div', 'batch-switch-toggle');
    input = element('input');
    input.type = 'checkbox';
    input.checked = value !== undefined ? Boolean(value) : Boolean(field.default);
    const slider = element('span', 'batch-switch-slider');
    slider.setAttribute('aria-hidden', 'true');
    toggleWrap.append(input, slider);

    wrapper.append(infoWrap, toggleWrap);
  } else if (isRange) {
    wrapper = element('label', 'batch-setting batch-setting-range');
    if (field.span === 'full') wrapper.classList.add('batch-setting-full');
    else if (field.span) wrapper.style.gridColumn = `span ${field.span}`;

    const header = element('div', 'batch-range-header');
    header.append(element('span', 'batch-setting-label', field.label));
    const output = element('output', 'batch-range-badge', `${value ?? field.default ?? ''}${field.suffix || ''}`);
    header.append(output);

    input = element('input');
    input.type = 'range';
    if (field.min !== undefined) input.min = field.min;
    if (field.max !== undefined) input.max = field.max;
    if (field.step !== undefined) input.step = field.step;
    input.value = value ?? field.default ?? '';

    input.addEventListener('input', () => {
      output.value = `${input.value}${field.suffix || ''}`;
    });

    wrapper.append(header, input);
  } else if (isSelect) {
    const isHero = Boolean(field.isFormatHero);
    wrapper = element('div', isHero ? 'batch-setting batch-setting-select batch-format-hero batch-setting-full' : 'batch-setting batch-setting-select');
    if (field.span === 'full' && !isHero) wrapper.classList.add('batch-setting-full');
    else if (field.span && !isHero) wrapper.style.gridColumn = `span ${field.span}`;

    if (isHero) {
      const heroHeader = element('div', 'batch-format-hero-header');
      heroHeader.innerHTML = `
        <div class="batch-format-hero-icon" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M7 16V4m0 0L3 8m4-4l4 4m6 4v12m0 0l4-4m-4 4l-4-4"/>
          </svg>
        </div>
        <div class="batch-format-hero-text">
          <span class="batch-format-hero-label">${field.label}</span>
          <span class="batch-format-hero-badge">${field.badge || (isAr ? 'تحويل فوري فائق الدقة بدون رفع لخوادم خارجية' : 'Instant high-fidelity in-browser conversion')}</span>
        </div>
      `;
      wrapper.append(heroHeader);
    } else {
      wrapper.append(element('span', 'batch-setting-label', field.label));
    }

    const selectWrap = element('div', isHero ? 'batch-select-wrap batch-format-hero-select-wrap' : 'batch-select-wrap');
    input = element('select');
    for (const option of field.options || []) {
      const item = element('option', '', option.label);
      item.value = option.value;
      input.append(item);
    }
    input.value = value ?? field.default ?? '';
    selectWrap.append(input);
    enhanceSelect(input);
    wrapper.append(selectWrap);
    if (field.hint) {
      wrapper.append(element('small', 'batch-setting-hint', field.hint));
    }
  } else if (field.type === 'color') {
    wrapper = element('label', 'batch-setting batch-setting-color');
    if (field.span === 'full') wrapper.classList.add('batch-setting-full');
    else if (field.span) wrapper.style.gridColumn = `span ${field.span}`;

    wrapper.append(element('span', 'batch-setting-label', field.label));
    const colorWrap = element('div', 'batch-color-wrap');
    input = element('input');
    input.type = 'color';
    input.value = value ?? field.default ?? '#ffffff';
    const textVal = element('span', 'batch-color-val', input.value);
    input.addEventListener('input', () => {
      textVal.textContent = input.value;
    });
    colorWrap.append(input, textVal);
    wrapper.append(colorWrap);
    if (field.hint) {
      wrapper.append(element('small', 'batch-setting-hint', field.hint));
    }
  } else if (field.type === 'file') {
    wrapper = element('div', 'batch-setting batch-setting-file');
    if (field.span === 'full') wrapper.classList.add('batch-setting-full');
    else if (field.span) wrapper.style.gridColumn = `span ${field.span}`;

    wrapper.append(element('span', 'batch-setting-label', field.label));

    const miniDrop = element('div', 'kz-mini-dropzone');
    miniDrop.setAttribute('role', 'button');
    miniDrop.setAttribute('tabindex', '0');
    miniDrop.setAttribute('aria-label', field.label || (isAr ? 'رفع صورة الشعار' : 'Upload logo image'));

    input = element('input');
    input.type = 'file';
    input.accept = field.accept || 'image/*';
    input.className = 'kz-mini-file-input';
    input.hidden = true;

    const emptyView = element('div', 'kz-mini-dropzone-empty');
    emptyView.innerHTML = `
      <div class="kz-mini-icon-wrap" aria-hidden="true">
        <svg class="kz-mini-dropzone-icon" width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242M12 12v9m0-9l-3 3m3-3l3 3"/>
        </svg>
      </div>
      <div class="kz-mini-dropzone-text">
        <strong class="kz-mini-dropzone-title">${field.placeholder || (isAr ? 'اختر صورة الشعار أو اسحبها هنا' : 'Choose logo image or drag & drop here')}</strong>
        <span class="kz-mini-dropzone-sub">${isAr ? 'يدعم PNG شفاف أو WebP أو SVG · معالجة محلية 100%' : 'Supports transparent PNG, WebP, or SVG · 100% private'}</span>
      </div>
    `;

    const filledView = element('div', 'kz-mini-dropzone-filled');
    filledView.style.display = 'none';
    filledView.innerHTML = `
      <div class="kz-mini-thumb-checker" aria-hidden="true">
        <img class="kz-mini-thumb" alt="${isAr ? 'معاينة الشعار' : 'Logo preview'}">
      </div>
      <div class="kz-mini-file-info">
        <strong class="kz-mini-file-name"></strong>
        <span class="kz-mini-file-meta"></span>
      </div>
      <div class="kz-mini-file-actions">
        <button type="button" class="btn-secondary kz-mini-change-btn">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <span>${isAr ? 'تغيير' : 'Change'}</span>
        </button>
        <button type="button" class="kz-mini-remove-btn" title="${isAr ? 'إزالة الشعار' : 'Remove logo'}" aria-label="${isAr ? 'إزالة الشعار' : 'Remove logo'}">
          ✕
        </button>
      </div>
    `;

    const thumbImg = filledView.querySelector('.kz-mini-thumb');
    const nameEl = filledView.querySelector('.kz-mini-file-name');
    const metaEl = filledView.querySelector('.kz-mini-file-meta');
    const changeBtn = filledView.querySelector('.kz-mini-change-btn');
    const removeBtn = filledView.querySelector('.kz-mini-remove-btn');

    miniDrop.append(input, emptyView, filledView);
    wrapper.append(miniDrop);

    input._customValue = null;

    const handleFile = async (file) => {
      if (!file) return;
      try {
        const bitmap = await createImageBitmap(file);
        if (input._customValue && typeof input._customValue.close === 'function') {
          input._customValue.close();
        }
        input._customValue = bitmap;
        nameEl.textContent = file.name;
        metaEl.textContent = `${bitmap.width} × ${bitmap.height} px · ${formatBytes(file.size)}`;
        thumbImg.src = URL.createObjectURL(file);
        emptyView.style.display = 'none';
        filledView.style.display = 'flex';
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (err) {
        console.error('Failed to load logo image:', err);
      }
    };

    miniDrop.addEventListener('click', (e) => {
      if (e.target.closest('.kz-mini-remove-btn')) return;
      input.click();
    });

    miniDrop.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        input.click();
      }
    });

    miniDrop.addEventListener('dragover', (e) => {
      e.preventDefault();
      miniDrop.classList.add('is-dragging', 'dragover');
    });
    miniDrop.addEventListener('dragleave', () => miniDrop.classList.remove('is-dragging', 'dragover'));
    miniDrop.addEventListener('drop', (e) => {
      e.preventDefault();
      miniDrop.classList.remove('is-dragging', 'dragover');
      const file = e.dataTransfer?.files?.[0];
      if (file) handleFile(file);
    });

    input.addEventListener('change', () => {
      const file = input.files?.[0];
      if (file) handleFile(file);
    });

    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      input.value = '';
      if (input._customValue && typeof input._customValue.close === 'function') {
        input._customValue.close();
      }
      input._customValue = null;
      filledView.style.display = 'none';
      emptyView.style.display = 'flex';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    if (field.hint) {
      wrapper.append(element('small', 'batch-setting-hint', field.hint));
    }
  } else if (field.type === 'position-grid') {
    wrapper = element('div', 'batch-setting batch-setting-position-grid');
    if (field.span === 'full') wrapper.classList.add('batch-setting-full');
    else if (field.span) wrapper.style.gridColumn = `span ${field.span}`;

    const head = element('div', 'batch-position-header');
    head.append(element('span', 'batch-setting-label', field.label));
    const badge = element('span', 'batch-position-badge');
    head.append(badge);

    input = element('input');
    input.type = 'hidden';
    input.value = value ?? field.default ?? 'bottom_right';

    const container = element('div', 'batch-position-container');
    const grid = element('div', 'batch-position-grid');
    grid.setAttribute('role', 'radiogroup');
    grid.style.direction = 'ltr';

    const positions = [
      { key: 'top_left', label: isAr ? 'أعلى اليسار' : 'Top Left', symbol: '↖' },
      { key: 'top_center', label: isAr ? 'أعلى الوسط' : 'Top Center', symbol: '↑' },
      { key: 'top_right', label: isAr ? 'أعلى اليمين' : 'Top Right', symbol: '↗' },
      { key: 'center_left', label: isAr ? 'وسط اليسار' : 'Center Left', symbol: '←' },
      { key: 'center', label: isAr ? 'في المنتصف تماماً' : 'Center', symbol: '•' },
      { key: 'center_right', label: isAr ? 'وسط اليمين' : 'Center Right', symbol: '→' },
      { key: 'bottom_left', label: isAr ? 'أسفل اليسار' : 'Bottom Left', symbol: '↙' },
      { key: 'bottom_center', label: isAr ? 'أسفل الوسط' : 'Bottom Center', symbol: '↓' },
      { key: 'bottom_right', label: isAr ? 'أسفل اليمين' : 'Bottom Right', symbol: '↘' }
    ];

    const posButtons = [];
    for (const p of positions) {
      const btn = element('button', 'batch-position-cell', p.symbol);
      btn.type = 'button';
      btn.title = p.label;
      btn.setAttribute('aria-label', p.label);
      btn.dataset.pos = p.key;

      btn.addEventListener('click', () => {
        if (input.disabled) return;
        input.value = p.key;
        updateUI(p.key);
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      posButtons.push(btn);
      grid.append(btn);
    }

    const sideActions = element('div', 'batch-position-side-actions');
    const tileBtn = element('button', 'batch-position-mode-btn');
    tileBtn.type = 'button';
    tileBtn.innerHTML = `
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
        <rect x="3" y="3" width="7" height="7"></rect>
        <rect x="14" y="3" width="7" height="7"></rect>
        <rect x="14" y="14" width="7" height="7"></rect>
        <rect x="3" y="14" width="7" height="7"></rect>
      </svg>
      <span>${isAr ? 'تكرار قطري مائل (Pattern)' : 'Tiled Pattern'}</span>
    `;
    tileBtn.addEventListener('click', () => {
      if (input.disabled) return;
      input.value = 'tile';
      updateUI('tile');
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    const customBtn = element('button', 'batch-position-mode-btn');
    customBtn.type = 'button';
    customBtn.innerHTML = `
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
        <polyline points="5 9 2 12 5 15"></polyline>
        <polyline points="9 5 12 2 15 5"></polyline>
        <polyline points="15 19 12 22 9 19"></polyline>
        <polyline points="19 9 22 12 19 15"></polyline>
        <line x1="2" y1="12" x2="22" y2="12"></line>
        <line x1="12" y1="2" x2="12" y2="22"></line>
      </svg>
      <span>${isAr ? 'موضع حر (سحب مباشر على الصورة) ✥' : 'Freeform Drag on Image ✥'}</span>
    `;
    customBtn.addEventListener('click', () => {
      if (input.disabled) return;
      input.value = 'custom';
      updateUI('custom');
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    sideActions.append(tileBtn, customBtn);
    container.append(grid, sideActions);
    wrapper.append(head, container, input);

    function updateUI(activeKey) {
      for (const btn of posButtons) {
        const isMatch = btn.dataset.pos === activeKey;
        btn.classList.toggle('is-active', isMatch);
      }
      tileBtn.classList.toggle('is-active', activeKey === 'tile');
      customBtn.classList.toggle('is-active', activeKey === 'custom');

      const found = positions.find((p) => p.key === activeKey);
      if (found) {
        badge.textContent = found.label;
      } else if (activeKey === 'tile') {
        badge.textContent = isAr ? 'تكرار قطري عبر كامل الصورة' : 'Tiled Pattern';
      } else if (activeKey === 'custom') {
        badge.textContent = isAr ? 'موضع حر (سحب يدوي)' : 'Custom Freeform Position';
      } else {
        badge.textContent = activeKey;
      }
    }

    input._updatePills = (val) => {
      input.value = val;
      updateUI(val);
    };

    updateUI(input.value);

    if (field.hint) {
      wrapper.append(element('small', 'batch-setting-hint', field.hint));
    }
  } else if (field.type === 'hidden') {
    wrapper = element('div', 'batch-setting-hidden');
    wrapper.hidden = true;
    wrapper.style.display = 'none';
    input = element('input');
    input.type = 'hidden';
    input.value = value ?? field.default ?? '';
    wrapper.append(input);
  } else {
    // Number or Text
    wrapper = element('label', 'batch-setting batch-setting-input');
    if (field.span === 'full') wrapper.classList.add('batch-setting-full');
    else if (field.span) wrapper.style.gridColumn = `span ${field.span}`;

    wrapper.append(element('span', 'batch-setting-label', field.label));
    const inputWrap = element('div', 'batch-input-wrap');
    input = element('input');
    input.type = field.type || 'text';
    if (field.inputMode) input.inputMode = field.inputMode;
    if (input.type === 'text') {
      input.addEventListener('focus', () => {
        const v = String(input.value).trim().toLowerCase();
        if (v === 'auto' || v === 'تلقائي') {
          setTimeout(() => input.select(), 10);
        }
      });
    }
    if (field.placeholder) input.placeholder = field.placeholder;
    if (field.min !== undefined) input.min = field.min;
    if (field.max !== undefined) input.max = field.max;
    if (field.step !== undefined) input.step = field.step;
    input.value = value ?? field.default ?? '';
    inputWrap.append(input);
    if (field.suffix) {
      inputWrap.append(element('span', 'batch-input-suffix', field.suffix));
    }
    wrapper.append(inputWrap);
    if (field.hint) {
      wrapper.append(element('small', 'batch-setting-hint', field.hint));
    }
  }

  return { wrapper, input };
}

function syncVisibility(currentSettings, wrappersMap, fieldsList) {
  for (const field of fieldsList) {
    if (typeof field.showIf === 'function') {
      const isVisible = Boolean(field.showIf(currentSettings));
      const wrap = wrappersMap.get(field.key);
      if (wrap) {
        wrap.hidden = !isVisible;
        wrap.style.display = isVisible ? '' : 'none';
      }
    }
  }
}

export function mountImageBatch({
  rootId,
  accept = 'image/*',
  fields = [],
  dropTitle,
  dropSubtitle,
  previewTitle,
  previewSubtitle,
  maxFiles = Infinity,
  maxFileBytes = Infinity,
  maxBatchBytes = Infinity,
  process,
  zipName = 'image-results.zip',
  initialFiles = [],
  onQueueChange,
  preview = true,
  renderPreview,
  onPreviewPointer,
  autoProcess = false
}) {
  const root = document.getElementById(rootId);
  if (!root) throw new Error(`Missing image batch workspace: #${rootId}`);
  root.classList.add('image-batch-tool');
  root.replaceChildren();

  const shouldAutoProcess = Boolean(autoProcess) || rootId === 'compressor-app';

  const toolSlug = (typeof window !== 'undefined' && window.__KZ_TOOL_SLUG__) ||
    window.location.pathname.replace(/^\/|\/$/g, '').split('/')[0] ||
    rootId.replace(/-app$/, '').replace(/^batch-/, '');

  let batchStartedTracked = false;
  let batchCompletedTracked = false;
  let hasFailureTrackedInCurrentRun = false;

  const toMb = (bytes) => {
    const mb = bytes / (1024 * 1024);
    return Number.isInteger(mb) ? String(mb) : mb.toFixed(1);
  };
  const finalTitle = dropTitle || common.dropTitle || common.drop;
  const limitText = Number.isFinite(maxFiles)
    ? (isAr
      ? `حتى ${maxFiles} صورة · ${toMb(maxFileBytes)} MB للصورة · ${toMb(maxBatchBytes)} MB للمجموعة`
      : `Up to ${maxFiles} images · ${toMb(maxFileBytes)} MB per file · ${toMb(maxBatchBytes)} MB per batch`)
    : (dropSubtitle || common.dropSubtitle);
  const tooLargeText = isAr
    ? `الحد الأقصى ${toMb(maxFileBytes)} MB للصورة و${toMb(maxBatchBytes)} MB للمجموعة.`
    : `Limits: ${toMb(maxFileBytes)} MB per file and ${toMb(maxBatchBytes)} MB per batch.`;
  const maxFilesText = isAr
    ? `الحد الأقصى ${maxFiles} صورة في القائمة.`
    : `The queue is limited to ${maxFiles} images.`;

  // Hidden File Picker
  const picker = element('input');
  picker.type = 'file';
  picker.accept = accept;
  picker.multiple = true;
  picker.className = 'batch-file-input';

  // Drag and Drop Zone (Generalizing the PDF Tools Dropzone Design)
  const drop = element('div', 'dropzone batch-dropzone');
  drop.setAttribute('role', 'button');
  drop.setAttribute('tabindex', '0');
  drop.setAttribute('aria-label', finalTitle);
  drop.innerHTML = `
    <div class="dropzone-content-default">
      <svg class="dropzone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">
        <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242M12 12v9m0-9l-3 3m3-3l3 3"/>
      </svg>
      <p class="dropzone-title">${finalTitle}</p>
      <p class="dropzone-subtitle">${limitText}</p>
    </div>
  `;
  drop.addEventListener('click', () => picker.click());
  drop.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      picker.click();
    }
  });
  drop.addEventListener('dragover', (event) => {
    event.preventDefault();
    drop.classList.add('dragover', 'is-dragging');
  });
  drop.addEventListener('dragleave', () => drop.classList.remove('dragover', 'is-dragging'));

  // 1. Dedicated Settings Panel (Only configuration fields & apply-to-all)
  const settingsPanel = element('section', 'batch-settings-panel');
  const globalFields = new Map();
  const globalWrappers = new Map();
  let applyAll = null;
  let syncIndicator = null;
  let updateSyncIndicator = () => {};
  const defaultValues = () => Object.fromEntries([...globalFields].map(([key, input]) => [key, input.type === 'checkbox' ? input.checked : (input._customValue !== undefined ? input._customValue : input.value)]));

  // Universal Live Preview Stage
  let previewStage = null;
  let canvasEl = null;
  let statsEl = null;
  let switcherEl = null;
  let stageWrap = null;
  let stageToolbar = null;
  let overlayEl = null;
  let overlayTitleEl = null;
  let overlaySubEl = null;
  let stageSyncWrap = null;
  let stageSyncBadge = null;
  let stageSyncText = null;
  let stageSyncTooltip = null;
  let stageApplyAllBtn = null;
  let stageRevertBtn = null;
  let updateStageSyncUI = () => {};
  let activePreviewItem = null;
  let activeBitmap = null;
  let overlayProgressWrap = null;
  let overlayProgressBar = null;
  let overlayPctEl = null;

  const showOverlay = (title, sub, pct) => {
    if (!overlayEl) return;
    if (overlayTitleEl) overlayTitleEl.textContent = title || '';
    if (overlaySubEl) overlaySubEl.textContent = sub || '';
    if (overlayProgressWrap && overlayProgressBar) {
      if (pct !== undefined && pct !== null && !isNaN(Number(pct))) {
        const clamped = Math.min(100, Math.max(0, Math.round(Number(pct))));
        overlayProgressWrap.hidden = false;
        overlayProgressBar.style.width = `${clamped}%`;
        if (overlayPctEl) overlayPctEl.textContent = `${clamped}%`;
      } else {
        overlayProgressWrap.hidden = true;
      }
    }
    overlayEl.hidden = false;
  };

  const hideOverlay = () => {
    if (overlayEl) overlayEl.hidden = true;
  };

  function getActiveSettings() {
    const global = defaultValues();
    if (!activePreviewItem) return global;
    if (queue.length > 1 && activePreviewItem._manuallyCustomized) {
      return { ...global, ...(activePreviewItem.settings || {}) };
    }
    return { ...(activePreviewItem.settings || {}), ...global };
  }

  if (preview !== false) {
    previewStage = element('section', 'batch-live-stage');
    previewStage.hidden = true;
    const stageTitle = previewTitle || (isAr ? 'المعاينة' : 'Viewer');
    const stageHint = previewSubtitle || (isAr ? 'معاينة مباشرة للنتيجة' : 'Live preview & result');

    previewStage.innerHTML = `
      <header class="batch-stage-header">
        <div class="batch-stage-title-wrap">
          <h3 class="batch-stage-title">${stageTitle}</h3>
          <span class="batch-stage-hint">${stageHint}</span>
        </div>
        <div class="batch-stage-actions">
          <div class="batch-stage-sync-wrap" hidden>
            <div class="batch-stage-sync-badge is-synced" tabindex="0" role="status">
              <span class="batch-stage-sync-dot" aria-hidden="true"></span>
              <span class="batch-stage-sync-text"></span>
              <div class="batch-stage-sync-tooltip" role="tooltip"></div>
            </div>
            <button type="button" class="batch-stage-apply-btn" hidden>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                <polyline points="9 14 11 16 15 11"></polyline>
              </svg>
              <span>${isAr ? 'تطبيق على الكل' : 'Apply to all'}</span>
            </button>
            <button type="button" class="batch-stage-revert-btn" title="${isAr ? 'إلغاء التخصيص واستعادة الإعدادات العامة' : 'Discard custom edits and restore global settings'}" hidden>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
              </svg>
              <span>${isAr ? 'استعادة التزامن' : 'Reset sync'}</span>
            </button>
          </div>
          <div class="batch-stage-stats">
            <span class="batch-stat-badge">—</span>
          </div>
        </div>
      </header>
      <div class="batch-stage-toolbar" hidden></div>
      <div class="batch-canvas-wrapper">
        <canvas class="batch-canvas"></canvas>
        <div class="batch-canvas-overlay" hidden>
          <div class="batch-overlay-card">
            <div class="batch-overlay-spinner" aria-hidden="true"></div>
            <div class="batch-overlay-title"></div>
            <div class="batch-overlay-sub"></div>
            <div class="batch-overlay-progress-wrap" hidden>
              <div class="batch-overlay-progress-track">
                <div class="batch-overlay-progress-bar" style="width:0%"></div>
              </div>
              <span class="batch-overlay-pct">0%</span>
            </div>
          </div>
        </div>
      </div>
      <div class="batch-image-switcher" hidden></div>
    `;
    canvasEl = previewStage.querySelector('.batch-canvas');
    statsEl = previewStage.querySelector('.batch-stat-badge');
    switcherEl = previewStage.querySelector('.batch-image-switcher');
    stageWrap = previewStage.querySelector('.batch-canvas-wrapper');
    stageToolbar = previewStage.querySelector('.batch-stage-toolbar');
    overlayEl = previewStage.querySelector('.batch-canvas-overlay');
    overlayTitleEl = previewStage.querySelector('.batch-overlay-title');
    overlaySubEl = previewStage.querySelector('.batch-overlay-sub');
    overlayProgressWrap = previewStage.querySelector('.batch-overlay-progress-wrap');
    overlayProgressBar = previewStage.querySelector('.batch-overlay-progress-bar');
    overlayPctEl = previewStage.querySelector('.batch-overlay-pct');
    stageSyncWrap = previewStage.querySelector('.batch-stage-sync-wrap');
    stageSyncBadge = previewStage.querySelector('.batch-stage-sync-badge');
    stageSyncText = previewStage.querySelector('.batch-stage-sync-text');
    stageSyncTooltip = previewStage.querySelector('.batch-stage-sync-tooltip');
    stageApplyAllBtn = previewStage.querySelector('.batch-stage-apply-btn');
    stageRevertBtn = previewStage.querySelector('.batch-stage-revert-btn');

    stageApplyAllBtn?.addEventListener('click', applyActiveItemToAll);
    stageRevertBtn?.addEventListener('click', revertActiveItemToSync);

    updateStageSyncUI = () => {
      if (!stageSyncWrap) return;
      if (queue.length <= 1 || !activePreviewItem) {
        stageSyncWrap.hidden = true;
        return;
      }
      stageSyncWrap.hidden = false;
      const isCustom = Boolean(activePreviewItem._manuallyCustomized);

      stageSyncBadge.className = `batch-stage-sync-badge ${isCustom ? 'is-custom' : 'is-synced'}`;
      const syncTitle = isCustom
        ? (isAr ? 'تعديل خاص: هذه الصورة لها إعدادات مستقلة منفصلة عن باقي الصور. انقر "تطبيق على الكل" لتطبيقها على جميع الصور في القائمة.' : 'Customized: This image has its own settings. Click "Apply to all" to propagate to all images in the queue.')
        : (isAr ? 'تزامن تلقائي: هذه الصورة تطبق الإعدادات العامة تلقائياً مع باقي الصور. أي تغيير في الإعدادات يطبق عليها فوراً.' : 'Auto-synced: This image uses the shared batch settings. Any global change applies here automatically.');
      stageSyncBadge.title = syncTitle;
      stageSyncBadge.setAttribute('aria-label', syncTitle);

      stageSyncText.textContent = isCustom
        ? (isAr ? 'إعدادات خاصة' : 'Custom settings')
        : (isAr ? 'متزامن تلقائياً' : 'Auto-synced');

      if (stageSyncTooltip) {
        stageSyncTooltip.textContent = syncTitle;
      }

      if (stageApplyAllBtn) {
        stageApplyAllBtn.hidden = !isCustom;
      }
      if (stageRevertBtn) {
        stageRevertBtn.hidden = !isCustom;
      }
    };

    if (typeof onPreviewPointer === 'function') {
      onPreviewPointer({
        stageWrap,
        canvasEl,
        stageToolbar,
        getActiveItem: () => activePreviewItem,
        getBitmap: () => activeBitmap,
        getSettings: () => getActiveSettings(),
        getQueue: () => queue,
        getQueueLength: () => queue.length,
        notifyItemChanged: (item) => {
          if (item) {
            renderItem(item);
            updateSyncIndicator();
            updateStageSyncUI();
          }
        },
        setSetting: (key, val) => {
          const ctrl = globalFields.get(key);
          if (ctrl) {
            if (ctrl.type === 'checkbox') ctrl.checked = Boolean(val);
            else if (typeof ctrl._updatePills === 'function') ctrl._updatePills(val);
            else ctrl.value = val;
            ctrl.dispatchEvent(new Event('input', { bubbles: true }));
            ctrl.dispatchEvent(new Event('change', { bubbles: true }));
          }
        },
        drawPreview: () => drawLivePreview(),
        showOverlay,
        hideOverlay
      });
    }
  }

  function updateControlUI(ctrl, val, field) {
    if (!ctrl) return;
    if (ctrl.type === 'checkbox') {
      ctrl.checked = Boolean(val);
    } else if (typeof ctrl._updatePills === 'function') {
      ctrl._updatePills(val);
    } else {
      ctrl.value = val !== undefined && val !== null ? val : '';
      if (ctrl.tagName === 'SELECT') {
        const triggerText = ctrl.parentElement?.querySelector('.kz-select-value');
        if (triggerText) {
          const opt = ctrl.querySelector(`option[value="${val}"]`);
          if (opt) triggerText.textContent = opt.textContent;
        }
      }
      const colorVal = ctrl.parentElement?.querySelector('.batch-color-val');
      if (colorVal) {
        colorVal.textContent = ctrl.value;
      }
    }
    const rangeBadge = ctrl.parentElement?.querySelector('.batch-range-badge');
    if (rangeBadge && field) {
      rangeBadge.value = `${ctrl.value}${field.suffix || ''}`;
    }
  }

  function applyActiveItemToAll() {
    if (!activePreviewItem) return;
    const itemSettings = { ...(activePreviewItem.settings || {}) };

    for (const field of fields) {
      const val = itemSettings[field.key];
      const ctrl = globalFields.get(field.key);
      if (ctrl && val !== undefined) {
        updateControlUI(ctrl, val, field);
      }
    }

    for (const item of queue) {
      item._suppressManualSync = true;
      item.settings = { ...itemSettings };
      item._manuallyCustomized = false;
      if (item.controls) {
        for (const field of fields) {
          const val = itemSettings[field.key];
          const ctrl = item.controls.get(field.key);
          if (ctrl && val !== undefined) {
            updateControlUI(ctrl, val, field);
          }
        }
        if (item.wrappers) {
          syncVisibility(item.settings, item.wrappers, fields);
        }
      }
      item._suppressManualSync = false;
      item.result = null;
      item.progress = 0;
      item.message = common.queued;
      renderItem(item);
    }

    syncVisibility(defaultValues(), globalWrappers, fields);
    updateQueueState();
    updateSyncIndicator(isAr ? 'تم تطبيق الإعدادات على جميع الصور بنجاح' : 'Applied settings to all images');
    updateStageSyncUI();
    drawLivePreview();
  }

  function revertActiveItemToSync() {
    if (!activePreviewItem) return;
    const defaults = defaultValues();
    activePreviewItem._suppressManualSync = true;
    activePreviewItem.settings = { ...defaults };
    activePreviewItem._manuallyCustomized = false;
    if (activePreviewItem.controls) {
      for (const field of fields) {
        const val = defaults[field.key];
        const ctrl = activePreviewItem.controls.get(field.key);
        if (ctrl && val !== undefined) {
          updateControlUI(ctrl, val, field);
        }
      }
      if (activePreviewItem.wrappers) {
        syncVisibility(activePreviewItem.settings, activePreviewItem.wrappers, fields);
      }
    }
    activePreviewItem._suppressManualSync = false;
    activePreviewItem.result = null;
    activePreviewItem.progress = 0;
    activePreviewItem.message = common.queued;
    renderItem(activePreviewItem);
    updateSyncIndicator();
    updateStageSyncUI();
    drawLivePreview();
  }

  if (fields.length) {
    const settingsHead = element('div', 'batch-settings-header');
    const titleWrap = element('div', 'batch-panel-title-wrap');
    titleWrap.innerHTML = `
      <div class="batch-title-icon-box" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
      </div>
      <div class="batch-title-text-box">
        <strong class="batch-panel-title">${common.defaults}</strong>
        <span class="batch-panel-desc">${isAr ? 'تُطبّق تلقائياً على كل الصور المُضافة' : 'Automatically applied to all added images'}</span>
      </div>
    `;
    const headActions = element('div', 'batch-settings-actions');
    syncIndicator = element('div', 'batch-sync-indicator');
    syncIndicator.innerHTML = `
      <span class="batch-sync-dot" aria-hidden="true"></span>
      <span class="batch-sync-text">${isAr ? 'مُطبّق تلقائياً على كل الصور' : 'Auto-applied to all images'}</span>
    `;

    applyAll = element('button', 'btn-secondary batch-apply-btn');
    applyAll.type = 'button';
    applyAll.hidden = true;
    applyAll.innerHTML = `
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" aria-hidden="true">
        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
        <polyline points="9 14 11 16 15 11"></polyline>
      </svg>
      <span>${common.apply}</span>
    `;
    headActions.append(syncIndicator, applyAll);
    settingsHead.append(titleWrap, headActions);

    updateSyncIndicator = (flashMsg = null) => {
      if (!syncIndicator) return;
      if (queue.length <= 1) {
        if (queue.length === 1) {
          queue[0]._manuallyCustomized = false;
        }
        syncIndicator.classList.remove('has-custom', 'is-updating');
        syncIndicator.querySelector('.batch-sync-text').textContent = queue.length === 1
          ? (isAr ? 'مُطبّق تلقائياً' : 'Auto-applied')
          : (isAr ? 'الإعدادات الافتراضية' : 'Default settings');
        if (applyAll) {
          applyAll.hidden = true;
        }
        updateStageSyncUI();
        return;
      }

      const customCount = queue.filter((item) => item._manuallyCustomized).length;
      if (flashMsg) {
        syncIndicator.classList.add('is-updating');
        syncIndicator.querySelector('.batch-sync-text').textContent = flashMsg;
        clearTimeout(syncIndicator._timer);
        syncIndicator._timer = setTimeout(() => {
          syncIndicator.classList.remove('is-updating');
          updateSyncIndicator();
        }, 1300);
        updateStageSyncUI();
        return;
      }
      if (customCount > 0) {
        syncIndicator.classList.add('has-custom');
        syncIndicator.classList.remove('is-updating');
        syncIndicator.querySelector('.batch-sync-text').textContent = isAr
          ? `${customCount} صورة بتعديل يدوي خاص`
          : `${customCount} image(s) with custom settings`;
        if (applyAll) {
          applyAll.hidden = false;
          applyAll.querySelector('span').textContent = isAr ? 'استعادة التزامن العام للكل' : 'Reset all to defaults';
        }
      } else {
        syncIndicator.classList.remove('has-custom', 'is-updating');
        syncIndicator.querySelector('.batch-sync-text').textContent = isAr
          ? 'مُطبّق تلقائياً على كل الصور'
          : 'Auto-applied to all images';
        if (applyAll) {
          applyAll.hidden = true;
        }
      }
      updateStageSyncUI();
    };

    const fieldGrid = element('div', 'batch-settings-grid');
    for (const field of fields) {
      const { wrapper, input } = readField(field);
      globalFields.set(field.key, input);
      globalWrappers.set(field.key, wrapper);

      if (field.placement === 'stage-toolbar' && stageToolbar) {
        stageToolbar.append(wrapper);
        stageToolbar.hidden = false;
      } else {
        fieldGrid.append(wrapper);
      }

      const onGlobalChange = () => {
        const current = defaultValues();
        const globalVal = input.type === 'checkbox' ? input.checked : (input._customValue !== undefined ? input._customValue : input.value);
        for (const item of queue) {
          if (queue.length <= 1 || !item._manuallyCustomized) {
            item._suppressManualSync = true;
            item.settings[field.key] = globalVal;
            if (item.controls) {
              const ctrl = item.controls.get(field.key);
              if (ctrl) {
                updateControlUI(ctrl, globalVal, field);
              }
              if (item.wrappers) {
                syncVisibility(item.settings, item.wrappers, fields);
              }
            }
            if (typeof field.onChange === 'function') {
              field.onChange({
                key: field.key,
                value: globalVal,
                settings: item.settings,
                controls: item.controls,
                isGlobal: false,
                item
              });
            }
            item._suppressManualSync = false;

            if (item.result) {
              item.result = null;
              item.progress = 0;
              item.message = common.queued;
              renderItem(item);
            }
          }
        }
        if (typeof field.onChange === 'function') {
          field.onChange({
            key: field.key,
            value: globalVal,
            settings: current,
            controls: globalFields,
            isGlobal: true,
            item: null
          });
        }
        syncVisibility(defaultValues(), globalWrappers, fields);
        if (queue.length > 0) {
          updateSyncIndicator();
        }
        if (typeof onQueueChange === 'function') {
          onQueueChange({ queue, total: queue.length });
        }
        drawLivePreview();
      };

      input.addEventListener('change', onGlobalChange);
      input.addEventListener('input', onGlobalChange);
    }

    settingsPanel.append(settingsHead, fieldGrid);
    syncVisibility(defaultValues(), globalWrappers, fields);
  }

  async function loadActivePreview(item) {
    if (!item?.file) return;
    activePreviewItem = item;
    try {
      if (activeBitmap) activeBitmap.close();
      activeBitmap = await createImageBitmap(item.file);
      drawLivePreview();
      updatePreviewSwitcher();
      updateStageSyncUI();
    } catch {
      // ignore
    }
  }

  function updatePreviewSwitcher() {
    if (!switcherEl) return;
    switcherEl.replaceChildren();
    if (queue.length <= 1) {
      switcherEl.hidden = true;
      return;
    }
    switcherEl.hidden = false;
    for (const item of queue) {
      const chip = element('button', `batch-thumb-chip ${item === activePreviewItem ? 'is-active' : ''}`);
      chip.type = 'button';
      chip.title = item.file.name;
      chip.setAttribute('aria-label', item.file.name);

      if (!item.originalUrl) {
        item.originalUrl = URL.createObjectURL(item.file);
      }
      const img = element('img', 'batch-chip-img');
      img.src = item.originalUrl;
      img.alt = item.file.name;
      img.loading = 'lazy';
      chip.append(img);

      chip.addEventListener('click', () => {
        loadActivePreview(item);
      });
      switcherEl.append(chip);
    }
  }

  let defaultPreviewSeq = 0;

  function drawLivePreview() {
    if (!previewStage || !canvasEl || !activeBitmap) return;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;
    const settings = getActiveSettings();

    if (typeof renderPreview === 'function') {
      renderPreview({
        canvas: canvasEl,
        ctx,
        bitmap: activeBitmap,
        settings,
        item: activePreviewItem,
        statsEl,
        stageWrap,
        stageToolbar,
        isAr,
        showOverlay,
        hideOverlay,
        setSetting: (key, val) => {
          const ctrl = globalFields.get(key);
          if (ctrl) {
            if (ctrl.type === 'checkbox') ctrl.checked = Boolean(val);
            else if (typeof ctrl._updatePills === 'function') ctrl._updatePills(val);
            else ctrl.value = val;
            ctrl.dispatchEvent(new Event('input', { bubbles: true }));
            ctrl.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
      return;
    }

    // Default universal preview: renders bitmap scaled cleanly with live scale and quality simulation
    const origW = activeBitmap.width;
    const origH = activeBitmap.height;
    const containerW = canvasEl.parentElement?.clientWidth || 720;
    const maxStageW = Math.min(760, containerW - 24);
    const maxStageH = 460;

    let targetW = origW;
    let targetH = origH;
    let scaleRatio = 1.0;

    if (settings.scale) {
      const sVal = settings.scale === 'custom'
        ? (Number(settings.customScale) || 80)
        : (Number(settings.scale) || 100);
      scaleRatio = Math.max(0.01, sVal / 100);
      targetW = Math.max(1, Math.round(origW * scaleRatio));
      targetH = Math.max(1, Math.round(origH * scaleRatio));
    } else if (settings.targetWidth || settings.targetHeight) {
      const parseNum = (v) => {
        if (!v) return 0;
        const s = String(v).trim().toLowerCase();
        if (s === 'auto' || s === 'تلقائي') return 0;
        const n = parseInt(s.replace(/[^0-9]/g, ''), 10);
        return Number.isFinite(n) && n > 0 ? n : 0;
      };
      const reqW = parseNum(settings.targetWidth);
      const reqH = parseNum(settings.targetHeight);
      if (reqW > 0 && reqH > 0) {
        targetW = reqW;
        targetH = reqH;
      } else if (reqW > 0) {
        targetW = reqW;
        targetH = Math.round(origH * (reqW / origW));
      } else if (reqH > 0) {
        targetH = reqH;
        targetW = Math.round(origW * (reqH / origH));
      }
      scaleRatio = Math.min(targetW / origW, targetH / origH);
    }

    const baseFitW = maxStageW * 0.70;
    const baseFitH = maxStageH * 0.70;
    let baseW, baseH;
    if ((targetW / targetH) >= (baseFitW / baseFitH)) {
      baseW = baseFitW;
      baseH = Math.round(baseW * (targetH / targetW));
    } else {
      baseH = baseFitH;
      baseW = Math.round(baseH * (targetW / targetH));
    }

    let displayW = Math.round(baseW * scaleRatio);
    let displayH = Math.round(baseH * scaleRatio);
    if (displayW > maxStageW || displayH > maxStageH) {
      const shrink = Math.min(maxStageW / displayW, maxStageH / displayH);
      displayW = Math.round(displayW * shrink);
      displayH = Math.round(displayH * shrink);
    }
    displayW = Math.max(64, displayW);
    displayH = Math.max(48, displayH);

    canvasEl.style.width = `${displayW}px`;
    canvasEl.style.height = `${displayH}px`;
    canvasEl.width = displayW;
    canvasEl.height = displayH;

    const isJpeg = settings.format === 'image/jpeg';
    if (isJpeg) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, displayW, displayH);
    }
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(activeBitmap, 0, 0, displayW, displayH);

    const fmt = (settings.format || '').split('/')[1]?.toUpperCase() || '';
    const qualPercent = Math.max(10, Math.min(100, Number(settings.quality) || 100));
    const qualText = (settings.format && settings.format !== 'image/png' && settings.quality) ? ` · Q:${qualPercent}%` : '';

    if (statsEl) {
      const dimText = (targetW !== origW || targetH !== origH)
        ? `${origW} × ${origH} px → ${targetW} × ${targetH} px`
        : `${origW} × ${origH} px`;
      statsEl.textContent = `${dimText}${fmt ? ` · ${fmt}` : ''}${qualText}`;
    }

    if (settings.format && settings.format !== 'image/png') {
      const renderSeq = ++defaultPreviewSeq;
      const targetMime = settings.format === 'image/jpeg' ? 'image/jpeg' : 'image/webp';
      const qual = qualPercent / 100;
      canvasEl.toBlob(async (blob) => {
        if (!blob || renderSeq !== defaultPreviewSeq) return;
        try {
          const compBmp = await createImageBitmap(blob);
          if (renderSeq !== defaultPreviewSeq) {
            compBmp.close();
            return;
          }
          if (isJpeg) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, displayW, displayH);
          } else {
            ctx.clearRect(0, 0, displayW, displayH);
          }
          ctx.drawImage(compBmp, 0, 0, displayW, displayH);
          compBmp.close();

          const bytesPerPx = blob.size / Math.max(1, displayW * displayH);
          const estTargetBytes = Math.round(bytesPerPx * (targetW * targetH));
          if (statsEl) {
            const dimText = (targetW !== origW || targetH !== origH)
              ? `${origW} × ${origH} px → ${targetW} × ${targetH} px`
              : `${origW} × ${origH} px`;
            statsEl.textContent = `${dimText} · ~${formatBytes(estTargetBytes)}${fmt ? ` · ${fmt}` : ''}${qualText}`;
          }
        } catch {
          // ignore
        }
      }, targetMime, qual);
    }
  }

  window.addEventListener('resize', () => {
    if (previewStage && !previewStage.hidden) drawLivePreview();
  });

  // 2. Dedicated Queue Control Bar (Separate from settings)
  const queueHeader = element('div', 'batch-queue-header is-empty');
  const queueMeta = element('div', 'batch-queue-meta');
  const queueBadge = element('span', 'batch-queue-badge', '0');
  const queueStatus = element('span', 'batch-queue-status', common.nothing);
  queueStatus.setAttribute('role', 'status');
  queueStatus.setAttribute('aria-live', 'polite');
  queueMeta.append(queueBadge, queueStatus);

  const queueActions = element('div', 'batch-queue-actions');

  // Prominent "Process All" Button (Moved to its own distinct place)
  const runAll = element('button', 'btn-primary batch-run-all-btn');
  runAll.type = 'button';
  runAll.innerHTML = `
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
      <polygon points="5 3 19 12 5 21 5 3"></polygon>
    </svg>
    <span>${common.processAll}</span>
  `;

  // "Download All ZIP" Button (Only visible if MORE THAN 1 image exists & completed)
  const downloadZip = element('button', 'btn-secondary batch-zip-btn');
  downloadZip.type = 'button';
  downloadZip.hidden = true; // Hidden by default!
  downloadZip.innerHTML = `
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
      <polyline points="7 10 12 15 17 10"></polyline>
      <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
    <span>${common.zip}</span>
  `;

  // Clear list button
  const clearAll = element('button', 'btn-tertiary batch-clear-btn', common.clear);
  clearAll.type = 'button';

  queueActions.append(runAll, downloadZip, clearAll);
  queueHeader.append(queueMeta, queueActions);

  const advisoryPill = element('div', 'file-advisory-pill');
  advisoryPill.hidden = true;
  advisoryPill.setAttribute('role', 'status');
  advisoryPill.setAttribute('aria-live', 'polite');
  advisoryPill.innerHTML = `
    <span class="file-advisory-icon" aria-hidden="true">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="12" cy="12" r="10"></circle>
        <line x1="12" y1="8" x2="12" y2="12"></line>
        <line x1="12" y1="16" x2="12.01" y2="16"></line>
      </svg>
    </span>
    <span class="file-advisory-text"></span>
  `;

  // 3. Compact Queue Items List
  const list = element('div', 'batch-queue-list');
  list.setAttribute('role', 'region');
  list.setAttribute('aria-label', isAr ? 'قائمة الصور' : 'Image queue');

  root.append(picker, drop, advisoryPill);
  if (fields.length) root.append(settingsPanel);
  if (previewStage) root.append(previewStage);
  root.append(queueHeader, list);

  // 4. Comparison Modal Dialog (Native <dialog> element)
  const compareDialog = element('dialog', 'batch-compare-dialog');
  compareDialog.setAttribute('aria-label', common.compareTitle);
  compareDialog.innerHTML = `
    <div class="compare-modal-box">
      <header class="compare-modal-header">
        <div class="compare-title-wrap">
          <h3 class="compare-modal-title"></h3>
          <span class="compare-modal-meta"></span>
        </div>
        <button type="button" class="compare-modal-close" aria-label="${common.close}">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </header>

      <div class="compare-stats-header">
        <div class="compare-stat-pill stat-before">
          <span class="compare-stat-label">${common.before}</span>
          <strong class="compare-stat-size compare-size-before">0 KB</strong>
        </div>
        <div class="compare-stat-diff">
          <span class="compare-diff-badge">-0%</span>
          <small class="compare-diff-saving"></small>
        </div>
        <div class="compare-stat-pill stat-after">
          <span class="compare-stat-label">${common.after}</span>
          <strong class="compare-stat-size compare-size-after">0 KB</strong>
        </div>
      </div>

      <div class="compare-stage" dir="ltr" style="--compare-pos: 50%;">
        <div class="compare-layer compare-layer-after">
          <img class="compare-img compare-img-after" alt="${common.after}" title="${common.after}">
          <span class="compare-badge-corner badge-after">${common.after}</span>
        </div>
        <div class="compare-layer compare-layer-before">
          <img class="compare-img compare-img-before" alt="${common.before}" title="${common.before}">
          <span class="compare-badge-corner badge-before">${common.before}</span>
        </div>
        <div class="compare-divider" aria-hidden="true">
          <div class="compare-handle">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
        </div>
        <input type="range" class="compare-range-input" min="0" max="100" value="50" step="0.1" aria-label="${common.compareTitle}">
      </div>

      <p class="compare-stage-hint">${common.dragToCompare}</p>

      <footer class="compare-modal-footer">
        <button type="button" class="btn-secondary compare-footer-close">${common.close}</button>
        <button type="button" class="btn-primary compare-footer-download">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          <span class="compare-download-label">${common.download}</span>
        </button>
      </footer>
    </div>
  `;
  root.append(compareDialog);

  // Dialog Controls
  const closeBtn = compareDialog.querySelector('.compare-modal-close');
  const footerCloseBtn = compareDialog.querySelector('.compare-footer-close');
  const downloadBtn = compareDialog.querySelector('.compare-footer-download');
  const compareStage = compareDialog.querySelector('.compare-stage');
  const compareSliderInput = compareDialog.querySelector('.compare-range-input');

  let currentCompareItem = null;
  downloadBtn.addEventListener('click', () => {
    if (!currentCompareItem?.result?.blob) return;
    const filename = currentCompareItem.result.filename || `${safeName(currentCompareItem.file.name)}.bin`;
    saveBlob(currentCompareItem.result.blob, filename);
  });

  const closeDialog = () => compareDialog.close();
  closeBtn.addEventListener('click', closeDialog);
  footerCloseBtn.addEventListener('click', closeDialog);
  compareDialog.addEventListener('click', (event) => {
    const modalBox = compareDialog.querySelector('.compare-modal-box');
    if (modalBox && !modalBox.contains(event.target)) closeDialog();
  });

  const setComparePosition = (percent) => {
    const clamped = Math.max(0, Math.min(100, Number(percent)));
    compareStage.style.setProperty('--compare-pos', `${clamped}%`);
    compareSliderInput.value = clamped;
  };
  compareSliderInput.addEventListener('input', () => setComparePosition(compareSliderInput.value));

  // Pointer drag on comparison viewport
  const handleStagePointer = (event) => {
    const rect = compareStage.getBoundingClientRect();
    if (rect.width <= 0) return;
    const x = event.clientX - rect.left;
    setComparePosition((x / rect.width) * 100);
  };

  compareStage.addEventListener('pointerdown', (event) => {
    handleStagePointer(event);
    if (compareStage.setPointerCapture) compareStage.setPointerCapture(event.pointerId);
    const onMove = (e) => handleStagePointer(e);
    const onUp = () => {
      compareStage.removeEventListener('pointermove', onMove);
      compareStage.removeEventListener('pointerup', onUp);
      compareStage.removeEventListener('pointercancel', onUp);
    };
    compareStage.addEventListener('pointermove', onMove);
    compareStage.addEventListener('pointerup', onUp);
    compareStage.addEventListener('pointercancel', onUp);
  });

  function openCompareModal(item) {
    if (!item.result?.blob || !item.outputUrl) return;
    const titleEl = compareDialog.querySelector('.compare-modal-title');
    const metaEl = compareDialog.querySelector('.compare-modal-meta');
    const imgBefore = compareDialog.querySelector('.compare-img-before');
    const imgAfter = compareDialog.querySelector('.compare-img-after');
    const sizeBefore = compareDialog.querySelector('.compare-size-before');
    const sizeAfter = compareDialog.querySelector('.compare-size-after');
    const diffBadge = compareDialog.querySelector('.compare-diff-badge');
    const diffSaving = compareDialog.querySelector('.compare-diff-saving');
    const badgeBefore = compareDialog.querySelector('.badge-before');
    const badgeAfter = compareDialog.querySelector('.badge-after');
    const downloadLink = compareDialog.querySelector('.compare-footer-download');
    const downloadLabel = compareDialog.querySelector('.compare-download-label');

    titleEl.textContent = item.file.name;
    const metaText = item.result.details ? item.result.details.split('·')[0].trim() : '';
    metaEl.textContent = metaText && !metaText.includes('%') ? metaText : '';

    imgBefore.src = item.originalUrl;
    imgAfter.src = item.outputUrl;

    const origBytes = item.file.size;
    const newBytes = item.result.blob.size;
    const savedPercent = Math.round((1 - newBytes / Math.max(origBytes, 1)) * 100);
    const savedBytes = Math.max(0, origBytes - newBytes);

    sizeBefore.textContent = formatBytes(origBytes);
    sizeAfter.textContent = formatBytes(newBytes);
    badgeBefore.textContent = `${common.before} · ${formatBytes(origBytes)}`;
    badgeAfter.textContent = `${common.after} · ${formatBytes(newBytes)}`;

    if (newBytes < origBytes) {
      const savedBytes = origBytes - newBytes;
      const savedPercent = Math.max(1, Math.round((savedBytes / origBytes) * 100));
      diffBadge.className = 'compare-diff-badge saving-good';
      diffBadge.textContent = `-${savedPercent}% ${common.smaller}`;
      diffSaving.textContent = isAr ? `وفرت ${formatBytes(savedBytes)}` : `Saved ${formatBytes(savedBytes)}`;
    } else if (newBytes > origBytes) {
      const extraBytes = newBytes - origBytes;
      const increasePercent = Math.max(1, Math.round((extraBytes / origBytes) * 100));
      diffBadge.className = 'compare-diff-badge saving-larger';
      diffBadge.textContent = `+${increasePercent}% ${isAr ? 'أكبر' : 'larger'}`;
      diffSaving.textContent = isAr ? `زيادة ${formatBytes(extraBytes)}` : `+${formatBytes(extraBytes)}`;
    } else {
      diffBadge.className = 'compare-diff-badge saving-neutral';
      diffBadge.textContent = isAr ? 'نفس الحجم (0%)' : 'Same size (0%)';
      diffSaving.textContent = '';
    }

    currentCompareItem = item;
    downloadLabel.textContent = `${common.download} (${formatBytes(newBytes)})`;

    setComparePosition(50);
    compareDialog.showModal();
  }

  const queue = [];
  let busy = false;

  function updateQueueState() {
    const total = queue.length;
    const readyItems = queue.filter((item) => item.result?.blob);
    const doneCount = readyItems.length;
    const pendingCount = queue.filter((item) => !item.result && !item.error).length;

    queueBadge.textContent = String(total);

    if (total === 0) {
      queueHeader.classList.add('is-empty');
      queueStatus.textContent = common.nothing;
      drop.classList.remove('is-compact');
      const titleEl = drop.querySelector('.dropzone-title');
      const subEl = drop.querySelector('.dropzone-subtitle');
      if (titleEl) titleEl.textContent = finalTitle;
      if (subEl) subEl.textContent = limitText;
      if (previewStage) {
        previewStage.hidden = true;
        activePreviewItem = null;
        if (activeBitmap) {
          activeBitmap.close();
          activeBitmap = null;
        }
      }
    } else {
      drop.classList.add('is-compact');
      const titleEl = drop.querySelector('.dropzone-title');
      const subEl = drop.querySelector('.dropzone-subtitle');
      if (titleEl) titleEl.textContent = isAr ? 'انقر أو اسحب لإضافة المزيد من الصور' : 'Click or drop to add more images';
      if (subEl) subEl.textContent = isAr ? `${total} ${total === 1 ? 'صورة' : 'صور'} في القائمة حالياً` : `${total} image(s) in queue`;

      if (previewStage) {
        previewStage.hidden = false;
        if (!activePreviewItem || !queue.includes(activePreviewItem)) {
          loadActivePreview(queue[0]);
        } else {
          drawLivePreview();
        }
        updatePreviewSwitcher();
      }

      if (doneCount === total && total > 0) {
        queueHeader.classList.remove('is-empty');
        queueStatus.textContent = isAr ? `اكتملت معالجة جميع الصور (${total})` : `All ${total} images processed`;
      } else if (doneCount > 0) {
        queueHeader.classList.remove('is-empty');
        queueStatus.textContent = isAr ? `اكتمل ${doneCount} من ${total} صورة` : `Completed ${doneCount} of ${total}`;
      } else {
        queueHeader.classList.remove('is-empty');
        queueStatus.textContent = isAr ? `${total} ${total === 1 ? 'صورة' : 'صور'} في قائمة الانتظار` : `${total} image(s) in queue`;
      }
    }

    if (total === 1 && queue[0]) {
      queue[0]._manuallyCustomized = false;
    }

    // Dynamic Run All / Re-process All button logic with informative titles and statuses
    if (busy) {
      runAll.disabled = true;
      runAll.classList.add('is-working');
      runAll.classList.remove('is-reprocess');
      runAll.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-dasharray="32" stroke-dashoffset="12" fill="none"/>
        </svg>
        <span>${isAr ? 'جارٍ المعالجة…' : 'Processing…'}</span>
      `;
      runAll.title = isAr ? 'تجري معالجة الصور الآن، يرجى الانتظار…' : 'Processing images, please wait…';
      runAll.setAttribute('aria-label', runAll.title);
    } else if (total === 0) {
      runAll.disabled = true;
      runAll.classList.remove('is-working', 'is-reprocess');
      runAll.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
          <polygon points="5 3 19 12 5 21 5 3"></polygon>
        </svg>
        <span>${common.processAll}</span>
      `;
      runAll.title = isAr ? 'أضف صوراً أولاً للبدء بالمعالجة' : 'Add images to start processing';
      runAll.setAttribute('aria-label', runAll.title);
    } else if (pendingCount > 0) {
      runAll.disabled = false;
      runAll.classList.remove('is-working', 'is-reprocess');
      const labelText = doneCount > 0
        ? (isAr ? `معالجة المتبقي (${pendingCount})` : `Process pending (${pendingCount})`)
        : (isAr ? `معالجة الكل (${total})` : `Process all (${total})`);
      runAll.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
          <polygon points="5 3 19 12 5 21 5 3"></polygon>
        </svg>
        <span>${labelText}</span>
      `;
      runAll.title = isAr ? `بدء معالجة ${pendingCount} من الصور غير المكتملة` : `Start processing ${pendingCount} pending images`;
      runAll.setAttribute('aria-label', runAll.title);
    } else if (doneCount === total && total > 0) {
      // ALL IMAGES ALREADY PROCESSED: change text to Re-process All and keep button ACTIVE and ENABLED!
      runAll.disabled = false;
      runAll.classList.remove('is-working');
      runAll.classList.add('is-reprocess');
      runAll.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
        </svg>
        <span>${common.reprocessAll}</span>
      `;
      runAll.title = isAr ? `إعادة معالجة جميع الصور (${total}) وتحديث النتائج وفق الإعدادات الحالية` : `Re-process all ${total} images with current settings`;
      runAll.setAttribute('aria-label', runAll.title);
    } else if (errorCount > 0) {
      runAll.disabled = false;
      runAll.classList.remove('is-working', 'is-reprocess');
      runAll.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67"/>
        </svg>
        <span>${isAr ? `إعادة محاولة الفاشلة (${errorCount})` : `Retry failed (${errorCount})`}</span>
      `;
      runAll.title = isAr ? `إعادة محاولة معالجة ${errorCount} من الصور الفاشلة` : `Retry ${errorCount} failed images`;
      runAll.setAttribute('aria-label', runAll.title);
    }

    if (applyAll) {
      applyAll.disabled = busy || total === 0;
    }
    clearAll.disabled = busy || total === 0;
    updateSyncIndicator();
    updateStageSyncUI();

    // Requirement: ZIP button must NOT appear at all unless there are > 1 images & ready results!
    if (total > 1 && doneCount > 0) {
      downloadZip.hidden = false;
      downloadZip.disabled = busy;
    } else {
      downloadZip.hidden = true;
    }

    const totalBytes = queue.reduce((sum, item) => sum + (item.file?.size || 0), 0);
    const advisoryTextEl = advisoryPill.querySelector('.file-advisory-text');
    if (total >= 20 && totalBytes >= 40 * 1024 * 1024) {
      if (advisoryTextEl) advisoryTextEl.textContent = isAr
        ? `مجموعة كبيرة (${total} صورة · ${toMb(totalBytes)} MB) — تجري المعالجة بالكامل محلياً داخل جهازك.`
        : `Large batch (${total} images · ${toMb(totalBytes)} MB) — processing 100% locally on your device.`;
      advisoryPill.hidden = false;
    } else if (total >= 20) {
      if (advisoryTextEl) advisoryTextEl.textContent = isAr
        ? `مجموعة متعددة (${total} صورة) — تتم المعالجة محلياً بدون خوادم، يُفضل إبقاء المتصفح نشطاً حتى الانتهاء.`
        : `Multi-image batch (${total} images) — 100% local processing; keep the browser tab active for best speed.`;
      advisoryPill.hidden = false;
    } else if (totalBytes >= 40 * 1024 * 1024) {
      if (advisoryTextEl) advisoryTextEl.textContent = isAr
        ? `حجم إجمالي كبير (${toMb(totalBytes)} MB) — تجري المعالجة في متصفحك، قد يستغرق التصدير بضع ثوانٍ إضافية.`
        : `Large batch size (${toMb(totalBytes)} MB) — processing locally; export may take a few moments.`;
      advisoryPill.hidden = false;
    } else {
      advisoryPill.hidden = true;
    }

    try {
      if (typeof onQueueChange === 'function') {
        onQueueChange({ queue, defaultValues: defaultValues(), defaultSettings: defaultValues() });
      }
    } catch { /* ignore */ }
  }

  function renderItem(item) {
    if (item.originalUrl) URL.revokeObjectURL(item.originalUrl);
    item.originalUrl = URL.createObjectURL(item.file);

    const card = element('article', 'batch-card');
    if (item._customOpen) card.classList.add('is-custom-open');
    card.dataset.status = item.result ? 'done' : item.error ? 'error' : item.progress > 0 ? 'working' : 'queued';

    // 1. Compact Main Horizontal Bar (No separate preview image!)
    const mainRow = element('div', 'batch-card-main');

    // Thumbnail
    const thumbWrap = element('div', 'batch-thumb-wrap');
    const thumb = element('img', 'batch-thumbnail');
    thumb.src = item.originalUrl;
    thumb.alt = '';
    thumb.loading = 'lazy';
    thumb.decoding = 'async';
    thumbWrap.append(thumb);

    // If item is done with image output, clicking thumbnail opens comparison, otherwise previews in live stage
    if (item.result?.blob?.type?.startsWith('image/')) {
      thumbWrap.classList.add('can-compare');
      thumbWrap.title = common.compareTitle;
      thumbWrap.addEventListener('click', () => openCompareModal(item));
    } else {
      thumbWrap.style.cursor = 'pointer';
      thumbWrap.title = isAr ? 'معاينة هذه الصورة' : 'Preview this image';
      thumbWrap.addEventListener('click', () => {
        if (previewStage) loadActivePreview(item);
      });
    }

    // File info & reduction stats
    const body = element('div', 'batch-card-body');
    const titleRow = element('div', 'batch-card-title-row');
    const title = element('strong', 'batch-file-name', item.file.name);
    title.title = isAr ? 'انقر لمعاينة هذه الصورة' : 'Click to preview this image';
    title.style.cursor = 'pointer';
    title.addEventListener('click', () => {
      if (previewStage) loadActivePreview(item);
    });
    titleRow.append(title);

    const statsRow = element('div', 'batch-card-stats');

    if (item.result) {
      // Completed state: show reduction data directly on this same strip!
      const origBytes = item.file.size;
      const newBytes = item.result.blob?.size || 0;
      const savedPercent = Math.round((1 - newBytes / Math.max(origBytes, 1)) * 100);

      const flowWrap = element('span', 'batch-stat-flow');
      flowWrap.setAttribute('dir', 'ltr');
      const origSpan = element('span', 'batch-stat-old', formatBytes(origBytes));
      const arrowSpan = element('span', 'batch-stat-arrow', '→');
      arrowSpan.setAttribute('aria-hidden', 'true');
      const newSpan = element('strong', 'batch-stat-new', formatBytes(newBytes));
      flowWrap.append(origSpan, arrowSpan, newSpan);

      statsRow.append(flowWrap);

      if (newBytes < origBytes) {
        const savedBytes = origBytes - newBytes;
        const savedPercent = Math.max(1, Math.round((savedBytes / origBytes) * 100));
        const badge = element('span', 'batch-saving-badge saving-good', `-${savedPercent}% ${common.smaller}`);
        statsRow.append(badge);
      } else if (newBytes > origBytes) {
        const extraBytes = newBytes - origBytes;
        const increasePercent = Math.max(1, Math.round((extraBytes / origBytes) * 100));
        const badge = element('span', 'batch-saving-badge saving-larger', `+${increasePercent}% ${isAr ? 'أكبر' : 'larger'}`);
        statsRow.append(badge);
      } else {
        const badge = element('span', 'batch-saving-badge saving-neutral', isAr ? 'نفس الحجم (0%)' : 'Same size (0%)');
        statsRow.append(badge);
      }

      if (item.result.details) {
        const metaText = item.result.details.split('·')[0].trim();
        if (metaText && !metaText.includes('%')) {
          const dimSpan = element('span', 'batch-stat-dim', metaText);
          statsRow.append(dimSpan);
        }
      }

      if (fields.length && queue.length > 1) {
        const syncPill = element('span', `batch-card-sync-pill ${item._manuallyCustomized ? 'is-custom' : 'is-synced'}`);
        syncPill.innerHTML = item._manuallyCustomized
          ? `<span>★ ${isAr ? 'مخصص يدوياً' : 'Manual'}</span>`
          : `<span>● ${isAr ? 'متزامن تلقائياً' : 'Auto-synced'}</span>`;
        statsRow.append(syncPill);
      }
    } else if (item.error) {
      const errorSpan = element('span', 'batch-error-text', item.error);
      statsRow.append(errorSpan);
    } else if (item.progress > 0) {
      const workingSpan = element('span', 'batch-progress-text', item.message || common.working);
      statsRow.append(workingSpan);
    } else {
      // Queued
      const origSpan = element('span', 'batch-stat-orig', formatBytes(item.file.size));
      const dimSpan = element('span', 'batch-stat-dim', item.origWidth ? `${item.origWidth} × ${item.origHeight} px` : '');
      item._dimLabel = dimSpan;
      const queuedSpan = element('span', 'batch-status-tag queued', common.queued);

      statsRow.append(origSpan, dimSpan);
      if (fields.length && queue.length > 1) {
        const syncPill = element('span', `batch-card-sync-pill ${item._manuallyCustomized ? 'is-custom' : 'is-synced'}`);
        syncPill.innerHTML = item._manuallyCustomized
          ? `<span>★ ${isAr ? 'مخصص يدوياً' : 'Manual'}</span>`
          : `<span>● ${isAr ? 'متزامن تلقائياً' : 'Auto-synced'}</span>`;
        statsRow.append(syncPill);
      }
      statsRow.append(queuedSpan);
    }

    body.append(titleRow, statsRow);

    // Card Action Buttons
    const actions = element('div', 'batch-card-actions');

    if (item.result) {
      // Compare Button (Only for image results)
      if (item.result.blob?.type?.startsWith('image/')) {
        const compareBtn = element('button', 'btn-secondary batch-btn-compare');
        compareBtn.type = 'button';
        compareBtn.innerHTML = `
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="3"></rect>
            <line x1="12" y1="3" x2="12" y2="21"></line>
          </svg>
          <span>${common.compare}</span>
        `;
        compareBtn.title = common.compareTitle;
        compareBtn.addEventListener('click', () => openCompareModal(item));
        actions.append(compareBtn);
      }

      // Direct Download Button
      const downloadBtn = element('a', 'btn-primary batch-btn-download');
      downloadBtn.href = item.outputUrl;
      downloadBtn.download = item.result.filename || `${safeName(item.file.name)}.bin`;
      downloadBtn.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
          <polyline points="7 10 12 15 17 10"></polyline>
          <line x1="12" y1="15" x2="12" y2="3"></line>
        </svg>
        <span>${common.download}</span>
      `;
      downloadBtn.addEventListener('click', (e) => {
        if (item.result?.blob) {
          e.preventDefault();
          saveBlob(item.result.blob, item.result.filename || `${safeName(item.file.name)}.bin`);
        }
      });
      actions.append(downloadBtn);
    } else {
      // Single Process Button
      const processBtn = element('button', 'btn-secondary batch-btn-process', common.process);
      processBtn.type = 'button';
      processBtn.disabled = busy;
      processBtn.addEventListener('click', () => runItem(item));
      actions.append(processBtn);
    }

    // Per-image customize settings toggle
    let customization = null;
    if (fields.length) {
      const custBtn = element('button', 'batch-icon-button batch-btn-customize');
      custBtn.type = 'button';
      custBtn.title = common.customize;
      custBtn.setAttribute('aria-label', common.customize);
      custBtn.classList.toggle('is-active', Boolean(item._customOpen));
      custBtn.innerHTML = `
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
      `;
      custBtn.addEventListener('click', () => {
        item._customOpen = !item._customOpen;
        card.classList.toggle('is-custom-open', Boolean(item._customOpen));
        if (customization) {
          customization.hidden = !item._customOpen;
          custBtn.classList.toggle('is-active', item._customOpen);
        }
      });
      actions.append(custBtn);
    }

    // Remove Button
    const removeBtn = element('button', 'batch-icon-button batch-btn-remove', '×');
    removeBtn.type = 'button';
    removeBtn.setAttribute('aria-label', `${common.remove} ${item.file.name}`);
    removeBtn.disabled = busy;
    removeBtn.addEventListener('click', () => {
      releaseItem(item);
      const idx = queue.indexOf(item);
      if (idx !== -1) queue.splice(idx, 1);
      card.remove();
      updateQueueState();
    });
    actions.append(removeBtn);

    mainRow.append(thumbWrap, body, actions);

    // Slim integrated progress bar
    const progressRow = element('div', 'batch-card-progress');
    const progressBar = element('progress', 'batch-progress-bar');
    progressBar.max = 100;
    progressBar.value = item.progress || 0;
    progressRow.append(progressBar);

    // Per-image custom settings collapsible
    if (fields.length) {
      customization = element('div', 'batch-item-custom-panel');
      customization.hidden = !item._customOpen;
      const itemGrid = element('div', 'batch-settings-grid batch-item-settings-grid');
      item.controls = new Map();
      const itemWrappers = new Map();
      for (const field of fields) {
        if (field.placement === 'stage-toolbar') continue;
        const { wrapper, input } = readField(field, item.settings[field.key]);
        input.disabled = busy;

        const onItemChange = () => {
          if (item._suppressManualSync) return;
          const val = input.type === 'checkbox' ? input.checked : input.value;
          item.settings[field.key] = val;
          if (queue.length > 1) {
            item._manuallyCustomized = true;
          } else {
            item._manuallyCustomized = false;
            const globalCtrl = globalFields.get(field.key);
            if (globalCtrl) {
              updateControlUI(globalCtrl, val, field);
            }
          }
          if (typeof field.onChange === 'function') {
            field.onChange({
              key: field.key,
              value: val,
              settings: item.settings,
              controls: item.controls,
              isGlobal: false,
              item
            });
          }
          syncVisibility(item.settings, itemWrappers, fields);

          item.result = null;
          item.error = null;
          item.progress = 0;
          item.message = common.queued;
          if (item.outputUrl) {
            URL.revokeObjectURL(item.outputUrl);
            item.outputUrl = null;
          }
          card.dataset.status = 'queued';
          renderItem(item);
          updateQueueState();
          updateSyncIndicator();
          if (typeof onQueueChange === 'function') {
            onQueueChange({ queue, total: queue.length });
          }
          drawLivePreview();
        };

        input.addEventListener('change', onItemChange);
        if (input.tagName === 'INPUT' && (input.type === 'number' || input.type === 'text' || input.type === 'range')) {
          input.addEventListener('input', onItemChange);
        }

        item.controls.set(field.key, input);
        itemWrappers.set(field.key, wrapper);
        itemGrid.append(wrapper);
      }
      item.wrappers = itemWrappers;

      if (item._manuallyCustomized) {
        const resetRow = element('div', 'batch-setting-full');
        const resetBtn = element('button', 'batch-card-reset-sync-btn');
        resetBtn.type = 'button';
        resetBtn.innerHTML = `
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
            <path d="M21 3v5h-5"></path>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
            <path d="M8 16H3v5"></path>
          </svg>
          <span>${isAr ? 'استعادة التزامن التلقائي مع الإعدادات العامة' : 'Reset to auto-sync with defaults'}</span>
        `;
        resetBtn.addEventListener('click', () => {
          const defaults = defaultValues();
          item.settings = { ...defaults };
          item._manuallyCustomized = false;
          renderItem(item);
          updateSyncIndicator();
          updateStageSyncUI();
          if (typeof onQueueChange === 'function') {
            onQueueChange({ queue, total: queue.length });
          }
        });
        resetRow.append(resetBtn);
        itemGrid.append(resetRow);
      }
      syncVisibility(item.settings, itemWrappers, fields);
      customization.append(itemGrid);
    }
    let detailsDrawer = null;
    if (item.result?.details && item.result.details.includes('\n')) {
      detailsDrawer = element('details', 'batch-details-drawer');
      const summary = element('summary', 'batch-details-summary');
      summary.append(element('span', '', isAr ? 'عرض البيانات المستخرجة' : 'View extracted data'));
      const drawerBody = element('div', 'batch-drawer-body');
      const pre = element('pre', 'batch-drawer-pre', item.result.details.split('\n').slice(1).join('\n').trim());
      const copyBtn = element('button', 'btn-secondary batch-btn-copy');
      copyBtn.type = 'button';
      copyBtn.textContent = isAr ? 'نسخ البيانات' : 'Copy data';
      copyBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        try {
          await navigator.clipboard.writeText(pre.textContent);
          copyBtn.textContent = isAr ? 'تم النسخ!' : 'Copied!';
          setTimeout(() => { copyBtn.textContent = isAr ? 'نسخ البيانات' : 'Copy data'; }, 1800);
        } catch {}
      });
      drawerBody.append(pre, copyBtn);
      detailsDrawer.append(summary, drawerBody);
    }

    card.append(mainRow, progressRow);
    if (detailsDrawer) card.append(detailsDrawer);
    if (customization) card.append(customization);

    const previous = item.card;
    item.card = card;
    item.progressEl = progressBar;
    item.actionsEl = actions;

    if (previous) previous.replaceWith(card);
    else list.append(card);
  }

  function releaseItem(item) {
    if (item.originalUrl) URL.revokeObjectURL(item.originalUrl);
    if (item.outputUrl) URL.revokeObjectURL(item.outputUrl);
  }

  function setBusy(value) {
    busy = value;
    root.classList.toggle('is-processing', value);
    for (const item of queue) {
      for (const control of item.controls?.values() || []) control.disabled = value;
      const buttons = item.actionsEl?.querySelectorAll('button');
      if (buttons) for (const b of buttons) b.disabled = value;
    }
    for (const control of globalFields.values()) control.disabled = value;
    updateQueueState();
  }

  async function runItem(item) {
    if (busy || !queue.includes(item)) return;
    if (!batchStartedTracked) {
      batchStartedTracked = true;
      trackEvent('tool_started', toolSlug);
    }
    setBusy(true);
    item.error = null;
    item.result = null;
    if (item.outputUrl) {
      URL.revokeObjectURL(item.outputUrl);
      item.outputUrl = null;
    }
    item.message = common.working;
    item.progress = 1;
    renderItem(item);

    try {
      const result = await process(item.file, { ...item.settings }, (percent, label) => {
        item.progress = Math.max(0, Math.min(100, Number(percent) || 0));
        item.message = label || common.working;
        if (item.progressEl) item.progressEl.value = item.progress;
      }, item);
      if (!result?.blob) throw new Error(isAr ? 'لم تنتج معالجة الملف نتيجة.' : 'Processing returned no result.');
      if (item.outputUrl) URL.revokeObjectURL(item.outputUrl);
      item.result = result;
      item.outputUrl = URL.createObjectURL(result.blob);
      item.progress = 100;
      item.message = common.done;
      renderItem(item);
      if (!batchCompletedTracked) {
        batchCompletedTracked = true;
        trackEvent('tool_completed', toolSlug);
      }
    } catch (error) {
      item.error = error?.message || common.failed;
      item.message = common.failed;
      item.progress = 0;
      renderItem(item);
      if (!hasFailureTrackedInCurrentRun) {
        hasFailureTrackedInCurrentRun = true;
        trackEvent('tool_failed', toolSlug);
      }
    } finally {
      setBusy(false);
    }
  }

  async function runAllItems(options = {}) {
    if (busy || !queue.length) return;

    const allDone = queue.length > 0 && queue.every((i) => i.result?.blob);
    const force = Boolean(options?.forceAll) || allDone;
    const hasErrorsOnly = queue.some((i) => i.error) && !queue.some((i) => !i.result && !i.error);

    const targets = queue.filter((item) => {
      if (force) return true;
      if (hasErrorsOnly) return Boolean(item.error);
      return !item.result?.blob;
    });

    if (!targets.length) return;

    if (!batchStartedTracked) {
      batchStartedTracked = true;
      trackEvent('tool_started', toolSlug);
    }
    hasFailureTrackedInCurrentRun = false;
    batchCompletedTracked = false;

    setBusy(true);

    const { concurrency } = getDeviceProcessingCapacity();

    for (const item of targets) {
      item.error = null;
      if (force) {
        item.result = null;
        if (item.outputUrl) {
          URL.revokeObjectURL(item.outputUrl);
          item.outputUrl = null;
        }
      }
      item.message = common.queued;
      item.progress = 0;
      renderItem(item);
    }

    let targetIdx = 0;
    let anySuccess = false;
    let anyFail = false;

    async function processNext() {
      while (targetIdx < targets.length) {
        const item = targets[targetIdx++];
        if (!item || !queue.includes(item)) continue;

        item.error = null;
        item.result = null;
        if (item.outputUrl) {
          URL.revokeObjectURL(item.outputUrl);
          item.outputUrl = null;
        }
        item.message = common.working;
        item.progress = 1;
        renderItem(item);

        try {
          const result = await process(item.file, { ...item.settings }, (percent, label) => {
            item.progress = Math.max(0, Math.min(100, Number(percent) || 0));
            item.message = label || common.working;
            if (item.progressEl) item.progressEl.value = item.progress;
          }, item);

          if (!result?.blob) throw new Error(isAr ? 'لم تنتج معالجة الملف نتيجة.' : 'Processing returned no result.');
          if (item.outputUrl) URL.revokeObjectURL(item.outputUrl);
          item.result = result;
          item.outputUrl = URL.createObjectURL(result.blob);
          item.progress = 100;
          item.message = common.done;
          anySuccess = true;
        } catch (error) {
          item.error = error?.message || common.failed;
          item.message = common.failed;
          item.progress = 0;
          anyFail = true;
        }

        renderItem(item);
        const doneTotal = queue.filter((i) => i.result?.blob).length;
        queueStatus.textContent = isAr
          ? `اكتملت معالجة ${doneTotal} من ${queue.length} صورة`
          : `Processed ${doneTotal} of ${queue.length} images`;
      }
    }

    const workerCount = Math.min(concurrency, targets.length);
    const pool = [];
    for (let w = 0; w < workerCount; w++) {
      pool.push(processNext());
    }

    await Promise.all(pool);

    if (anyFail && !hasFailureTrackedInCurrentRun) {
      hasFailureTrackedInCurrentRun = true;
      trackEvent('tool_failed', toolSlug);
    }
    if (anySuccess && !batchCompletedTracked) {
      batchCompletedTracked = true;
      trackEvent('tool_completed', toolSlug);
    }

    setBusy(false);
  }

  function addFiles(files) {
    batchCompletedTracked = false;
    hasFailureTrackedInCurrentRun = false;
    const candidates = Array.from(files || []);
    const accepted = candidates.filter((file) => accept.split(',').some((entry) => {
      const type = entry.trim();
      if (type === 'image/*') return file.type.startsWith('image/');
      if (type.startsWith('.')) return file.name.toLowerCase().endsWith(type.toLowerCase());
      return file.type === type;
    }));

    if (accepted.length !== candidates.length) {
      queueStatus.textContent = common.invalid;
    }
    const defaults = defaultValues();
    for (const file of accepted) {
      const item = {
        file,
        settings: { ...defaults },
        result: null,
        error: null,
        progress: 0,
        message: common.queued,
        originalUrl: null,
        outputUrl: null,
        origWidth: null,
        origHeight: null,
        _customOpen: false
      };
      createImageBitmap(file).then((bmp) => {
        item.origWidth = bmp.width;
        item.origHeight = bmp.height;
        bmp.close();
        if (item._dimLabel && !item.result) {
          item._dimLabel.textContent = `${item.origWidth} × ${item.origHeight} px`;
        }
      }).catch(() => {});
      queue.push(item);
      renderItem(item);
    }

    updateQueueState();
    // Auto-process on addition only for the image compressor
    if (shouldAutoProcess) {
      setTimeout(() => {
        if (!busy && queue.some((i) => !i.result && !i.error)) {
          runAllItems();
        }
      }, 50);
    }
  }

  picker.addEventListener('change', () => {
    addFiles(picker.files);
    picker.value = '';
  });

  drop.addEventListener('drop', (event) => {
    event.preventDefault();
    drop.classList.remove('dragover', 'is-dragging');
    addFiles(event.dataTransfer.files);
  });

  applyAll?.addEventListener('click', () => {
    const defaults = defaultValues();
    for (const item of queue) {
      item._suppressManualSync = true;
      item.settings = { ...defaults };
      item._manuallyCustomized = false;
      if (item.controls) {
        for (const [key, val] of Object.entries(defaults)) {
          const ctrl = item.controls.get(key);
          if (ctrl) {
            if (ctrl.type === 'checkbox') ctrl.checked = Boolean(val);
            else if (typeof ctrl._updatePills === 'function') ctrl._updatePills(val);
            else {
              ctrl.value = val;
              if (ctrl.tagName === 'SELECT') {
                const triggerText = ctrl.parentElement?.querySelector('.kz-select-value');
                if (triggerText) {
                  const opt = ctrl.querySelector(`option[value="${val}"]`);
                  if (opt) triggerText.textContent = opt.textContent;
                }
              }
            }
            const rangeBadge = ctrl.parentElement?.querySelector('.batch-range-badge');
            if (rangeBadge) {
              const field = fields.find((f) => f.key === key);
              rangeBadge.value = `${ctrl.value}${field?.suffix || ''}`;
            }
          }
        }
        if (item.wrappers) {
          syncVisibility(item.settings, item.wrappers, fields);
        }
      }
      item._suppressManualSync = false;
      item.result = null;
      item.error = null;
      item.progress = 0;
      item.message = common.queued;
      if (item.outputUrl) {
        URL.revokeObjectURL(item.outputUrl);
        item.outputUrl = null;
      }
      renderItem(item);
    }
    queueStatus.textContent = isAr ? 'تم تطبيق الإعدادات على جميع الصور.' : 'Settings applied to all images.';
    updateSyncIndicator(isAr ? 'تم استعادة التزامن العام لجميع الصور ✓' : 'Reset all images to defaults ✓');
    updateStageSyncUI();
    updateQueueState();
    if (typeof onQueueChange === 'function') {
      onQueueChange({ queue, total: queue.length });
    }
    drawLivePreview();
  });

  runAll.addEventListener('click', () => {
    const total = queue.length;
    const doneCount = queue.filter((item) => item.result?.blob).length;
    const forceAll = total > 0 && doneCount === total;
    runAllItems({ forceAll });
  });

  clearAll.addEventListener('click', () => {
    if (busy) return;
    queue.forEach(releaseItem);
    queue.length = 0;
    list.replaceChildren();
    updateQueueState();
  });

  downloadZip.addEventListener('click', async () => {
    const ready = queue.filter((item) => item.result?.blob);
    if (!ready.length || busy) return;
    if (!batchCompletedTracked) {
      batchCompletedTracked = true;
      trackEvent('tool_completed', toolSlug);
    }
    try {
      const archiveFiles = {};
      const names = new Map();
      const archiveSize = ready.reduce((sum, item) => sum + item.result.blob.size, 0);
      if (archiveSize > 1536 * 1024 * 1024) {
        throw new Error(isAr ? 'حجم النتائج أكبر من الحد الآمن لإنشاء ZIP في المتصفح. نزّل الصور فرديًا.' : 'Total size exceeds safe in-browser ZIP limit. Download files individually.');
      }
      for (const item of ready) {
        const proposed = (item.result.filename || `${safeName(item.file.name)}.bin`).replace(/^[/\\]+|\.\.(?:[/\\]|$)/g, '_');
        const count = (names.get(proposed) || 0) + 1;
        names.set(proposed, count);
        const dot = proposed.lastIndexOf('.');
        const name = count === 1 ? proposed : `${dot > 0 ? proposed.slice(0, dot) : proposed} (${count})${dot > 0 ? proposed.slice(dot) : ''}`;
        archiveFiles[name] = new Uint8Array(await item.result.blob.arrayBuffer());
      }
      saveBlob(new Blob([zipSync(archiveFiles, { level: 0 })], { type: 'application/zip' }), zipName);
      queueStatus.textContent = common.zipReady;
    } catch (error) {
      queueStatus.textContent = error?.message || (isAr ? 'تعذر إنشاء ملف ZIP.' : 'Could not create the ZIP archive.');
    }
  });

  if (initialFiles.length) addFiles(initialFiles);
  window.addEventListener('pagehide', () => queue.forEach(releaseItem), { once: true });

  return {
    addFiles,
    getItems: () => queue,
    setStatus: (text) => { queueStatus.textContent = text; },
    reRender: (item) => renderItem(item),
    toolbar: queueActions,
    isBusy: () => busy,
    root,
    settingsPanel,
    previewStage,
    showOverlay,
    hideOverlay,
    loadPreview: (item) => loadActivePreview(item),
    drawPreview: () => drawLivePreview(),
    getSettings: () => defaultValues(),
    setSetting: (key, val) => {
      const ctrl = globalFields.get(key);
      if (ctrl) {
        if (ctrl.type === 'checkbox') ctrl.checked = Boolean(val);
        else if (typeof ctrl._updatePills === 'function') ctrl._updatePills(val);
        else ctrl.value = val;
        ctrl.dispatchEvent(new Event('input', { bubbles: true }));
        ctrl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    },
    runExclusive: async (task) => {
      if (busy) return false;
      setBusy(true);
      try {
        await task();
        return true;
      } finally {
        setBusy(false);
      }
    }
  };
}

